import React, { Fragment, useEffect, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { equals, length, map } from "ramda";
import { Button, Paper, Popper, Grow, ClickAwayListener } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import FilterListIcon from "@mui/icons-material/FilterList";
import ContentFacetMenuItem from "./ContentFacetMenuItem";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import { caseInsensitiveSort } from "../../../util/sorts";

const useStyles = makeStyles(theme => ({
  applyButtonContainer: {
    padding: theme.spacing(0, 2, 2, 0),
  },

  filterButton: {
    width: "auto",
    padding: "inherit",
    fontWeight: "bold",
  },

  menu: {
    borderRadius: "5px",
    boxShadow: "0px 0px 30px rgba(149, 169, 177, 0.3)",
    width: "450px",
  },

  menuContainer: {
    zIndex: "1000",
  },

  menuHeader: {
    paddingBottom: 8,
  },

  selectedFilters: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 22,
    width: 22,
    borderRadius: "20px",
    backgroundColor: theme.palette.primary.brandPrimary,
    color: "white",
    fontWeight: "bold",
    marginLeft: theme.spacing(1),
  },

  startIcon: {
    marginLeft: 0,
  },

  title: {
    fontSize: "2rem",
    fontWeight: "bold",
    paddingLeft: theme.spacing(3),
  },
}));

const ContentFacetMenu = props => {
  const classes = useStyles();
  const {
    facets,
    handleFacetClick,
    handleFacetSelection,
    handleClearFilters,
    handleStatusClick,
    loading,
    selectedFacet,
    selectedFacets,
    totalSelectedTerms,
    useCustomStatusFacet = true,
  } = props;

  //-- Manage menu state
  const [openMenu, setOpenMenu] = useState(false);
  const anchorRef = useRef(null);
  const toggleMenu = () => setOpenMenu(!openMenu);
  const closeMenu = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenMenu(false);
  };

  const prevOpen = useRef(openMenu);
  useEffect(() => {
    if (prevOpen.current === true && openMenu === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = openMenu;
  }, [openMenu]);

  const handleClear = event => {
    handleClearFilters();
    closeMenu(event);
  };

  return (
    <Fragment>
      <Button
        ref={anchorRef}
        color="primary"
        onClick={toggleMenu}
        classes={{ root: classes.filterButton, startIcon: classes.startIcon }}
        startIcon={<FilterListIcon fontSize="large" />}
        style={{
          backgroundColor: openMenu && "rgba(0, 0, 0, 0.04",
        }}
        aria-controls={openMenu ? "content-facet-drop-down" : undefined}
        aria-haspopup="true"
      >
        <Grid
          container
          wrap="nowrap"
          alignItems="center"
          style={{ textAlign: "left" }}
        >
          <Grid item>Filters</Grid>
          {totalSelectedTerms > 0 && (
            <Grid item>
              <div className={classes.selectedFilters}>
                {totalSelectedTerms}
              </div>
            </Grid>
          )}
        </Grid>
      </Button>
      <Popper
        className={classes.menuContainer}
        open={openMenu}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement={"bottom-end"}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper classes={{ root: classes.menu }}>
              <ClickAwayListener onClickAway={closeMenu}>
                <List id="available-facets-drop-down">
                  <Grid
                    className={classes.menuHeader}
                    container
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography tabIndex={-1} className={classes.title}>
                      Filters
                    </Typography>
                    <Button variant="text" onClick={handleClear}>
                      Reset
                    </Button>
                  </Grid>
                  {loading ? (
                    <PanelLoadingIndicator />
                  ) : (
                    map(
                      (facet, idx) => (
                        <div key={idx}>
                          <ContentFacetMenuItem
                            useCustomStatusFacet={useCustomStatusFacet}
                            className={classes.facet}
                            facet={facet}
                            multiple={false}
                            handleClick={handleFacetClick}
                            selectedMenuItem={selectedFacet}
                            selectedFacets={selectedFacets}
                            handleFacetSelection={handleFacetSelection}
                            handleStatusClick={handleStatusClick}
                            index={idx}
                          />
                          {!equals(length(facets) - 1, idx) && (
                            <Divider light variant="middle" />
                          )}
                        </div>
                      ),
                      caseInsensitiveSort("displayName")(facets),
                    )
                  )}
                </List>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Fragment>
  );
};

export default ContentFacetMenu;
